import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ButtonFrameworkVariant } from "../../../Components/ButtonFramework/ButtonFramework";
import { awaitJSON, MetaLambdaFetch } from "../../../utils/fetch-utils";
import * as R from "ramda";
import { DROPDOWN_BTN, SEARCH_MENU } from "../../MetaBuyingConstants";
import { TargetingType, TargetingClass } from "@blisspointmedia/bpm-types/dist/MetaBuying";
import { MdArrowForwardIos, MdArrowBackIos } from "react-icons/md";
import { Form, Tooltip } from "react-bootstrap";
import { Button, ButtonType } from "../../../Components/Button/Button";
import { OverlayTrigger } from "../../../Components/OverlayTrigger";
import { Spinner } from "../../../Components/Spinner";

interface TargetingFilterMenuProps {
  placeholder: string;
  menuId: string;
  closeMenu: () => void;
  showMenu: boolean;
  applySelections: (value: Record<string, any>[]) => void;
  appliedSelections: Record<string, any>[];
  business_name: string;
  targetType: TargetingType;
  targetClass: TargetingClass;
  title?: string;
}

export const TargetingFilterMenu = ({
  placeholder,
  menuId,
  closeMenu,
  showMenu,
  applySelections,
  appliedSelections,
  business_name,
  targetType,
  targetClass,
  title,
}: TargetingFilterMenuProps): JSX.Element => {
  const [filter, setFilter] = useState("");
  const [selectedTargets, setSelectedTargets] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [page, setPage] = useState(0);

  const dropdownBtnId = [menuId, DROPDOWN_BTN].join("_");
  const searchMenuId = [menuId, SEARCH_MENU].join("_");

  useEffect(() => {
    setSelectedTargets(R.clone(appliedSelections));
  }, [showMenu, appliedSelections]);

  const fetchData = useCallback(async () => {
    let params: {
      business_name: string;
      targetType: TargetingType;
      targetClass: TargetingClass;
    } = {
      business_name,
      targetType,
      targetClass,
    };
    const res = await MetaLambdaFetch("/searchMetaDetailedTargeting", {
      params,
    });
    const { data } = await awaitJSON(res);
    setLoading(false);
    setData(data);
  }, [business_name, targetType, targetClass, setLoading, setData]);

  useEffect(() => {
    if (!showMenu) {
      setFilter("");
      setPage(0);
    } else {
      fetchData();
    }
  }, [showMenu, fetchData]);

  useEffect(() => {
    setPage(0);
  }, [filter]);

  const selectedIds: string[] = useMemo(() => selectedTargets.map(({ id }) => id), [
    selectedTargets,
  ]);

  const filteredData = useMemo(
    () =>
      filter.length
        ? data.filter(behavior =>
            R.includes(filter.toLocaleLowerCase(), behavior.name.toLocaleLowerCase())
          )
        : data,
    [filter, data]
  );

  return (
    <div id={dropdownBtnId} className="metaBuyingSearchMenu menuToggle">
      <div className="title">{title}</div>
      <Button className="dropdown-toggle" type={ButtonType.OUTLINED}>
        {appliedSelections.length > 0 ? (
          appliedSelections.map(({ name }, index) => (
            <div
              key={index}
              className="appliedSelection"
              onClick={() => {
                const newAppliedSelections = R.remove(index, 1, appliedSelections);
                applySelections(newAppliedSelections);
                setSelectedTargets(newAppliedSelections);
                closeMenu();
              }}
            >
              <span>{name}</span>
            </div>
          ))
        ) : (
          <span className="searchMenuPlaceholder">{placeholder}</span>
        )}
      </Button>
      {showMenu && (
        <div id={searchMenuId} className="searchMenu menuToggle">
          {" "}
          <div>
            {" "}
            {loading ? (
              <Spinner size={50} />
            ) : (
              <>
                <Form.Control
                  size="sm"
                  className="searchBar"
                  placeholder="Search"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                />
                <div className="searchMenuRows">
                  {filteredData.slice(page * 10, page * 10 + 10).map((option, index) => (
                    <OverlayTrigger
                      key={index}
                      placement="right center"
                      overlay={
                        (option.coverage_lower_bound && option.coverage_upper_bound) ||
                        (option.audience_size_lower_bound && option.audience_size_upper_bound) ? (
                          <Tooltip id={`id:${menuId}-row-${index}`}>
                            {option.description && (
                              <>
                                {option.description}
                                <br />
                              </>
                            )}
                            {`Size: ${(
                              option.coverage_lower_bound || option.audience_size_lower_bound
                            ).toLocaleString()} - ${(
                              option.coverage_upper_bound || option.audience_size_upper_bound
                            ).toLocaleString()}`}
                          </Tooltip>
                        ) : (
                          <div />
                        )
                      }
                    >
                      <div className="searchMenuRow">
                        <Form.Check
                          className="searchMenuOption"
                          key={index}
                          type="checkbox"
                          label={<div>{option.name}</div>}
                          checked={R.includes(option.id, selectedIds)}
                          id={option.name}
                          onChange={() =>
                            setSelectedTargets(
                              R.includes(option.id, selectedIds)
                                ? selectedTargets.filter(({ id }) => id !== option.id)
                                : [...selectedTargets, option]
                            )
                          }
                        />
                      </div>
                    </OverlayTrigger>
                  ))}
                </div>
              </>
            )}
            {filteredData.length > 10 && (
              <div className="searchMenuPaging">
                {page > 0 && (
                  <Button
                    type={ButtonType.OUTLINED}
                    variant={ButtonFrameworkVariant.LEADING_ICON}
                    onClick={() => setPage(page - 1)}
                    icon={<MdArrowBackIos />}
                    size="sm"
                    className="prevPage"
                  >
                    Prev
                  </Button>
                )}
                {filteredData.slice(page * 10).length > 10 && (
                  <Button
                    type={ButtonType.OUTLINED}
                    variant={ButtonFrameworkVariant.TRAILING_ICON}
                    onClick={() => setPage(page + 1)}
                    icon={<MdArrowForwardIos />}
                    size="sm"
                    className="nextPage"
                  >
                    Next
                  </Button>
                )}
              </div>
            )}
            <div className="searchMenuControls">
              <Button
                type={ButtonType.OUTLINED}
                variant={ButtonFrameworkVariant.NO_ICON}
                onClick={closeMenu}
              >
                Cancel
              </Button>
              <Button
                disabled={loading || R.isEmpty(selectedIds)}
                type={ButtonType.FILLED}
                variant={ButtonFrameworkVariant.NO_ICON}
                onClick={() => {
                  applySelections(selectedTargets);
                  closeMenu();
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
